import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {environment} from '@environments/environment';
import {BehaviorSubject, Observable, of, throwError} from 'rxjs';
import {QueryParamsModel} from '@core/models/query-models/query-params.model';
import {QueryResultsModel} from '@core/models/query-models/query-results.model';
import {catchError, finalize, map} from 'rxjs/operators';
import {HttpUtilsService} from '@core/utils/http-utils.service';
import {MaterialModel} from '../_models/material.model';

@Injectable({
    providedIn: 'root'
})
export class MaterialService {
    API_URL = `${environment.api_host}/api/raw-material/`;
    API_URL_MASTER = `${environment.api_host}/api/master/`;

    // tslint:disable-next-line:variable-name
    private _isLoading$ = new BehaviorSubject<boolean>(false);
    private _isLoadingOnboarding$ = new BehaviorSubject<null | boolean>(null);

    constructor(private http: HttpClient, private httpUtils: HttpUtilsService) {
    }

    findMaterials(queryParams?: QueryParamsModel): Observable<QueryResultsModel<MaterialModel>> {
        this._isLoading$.next(true);
        const offset = queryParams ? queryParams.pageNumber * queryParams.pageSize : 0;
        const limit = queryParams ? queryParams.pageSize : 100;

        let params = new HttpParams();
        if (queryParams?.filter) {
            params = new HttpParams({fromObject: queryParams?.filter});
        }

        if (queryParams?.sortField.length > 0 && queryParams?.sortOrder.length > 0) {
            params = params.append('sort_direction', queryParams ? queryParams?.sortOrder : '');
            params = params.append('sort_field', queryParams ? queryParams?.sortField : '');
        }

        if (queryParams?.search.length > 0) {
            params = params.append('search_term', queryParams ? queryParams?.search : '');
        }


        const url = this.API_URL + `list/${limit}/${offset}`;
        return this.http.get<QueryResultsModel<MaterialModel>>(url, {params}).pipe(
            map((res: QueryResultsModel<MaterialModel>) => {
                const queryResults = new QueryResultsModel<MaterialModel>();
                queryResults.results = res.results;
                queryResults.total = res.total;
                return queryResults;
            }),
            finalize(() => {
                this._isLoading$.next(false)
                if (this._isLoadingOnboarding$.getValue() === true) {
                    this._isLoadingOnboarding$.next(false)
                }

            })
        );
    }

    createMaterial(material: MaterialModel) {
        this._isLoading$.next(true);
        this._isLoadingOnboarding$.next(true);
        const url = this.API_URL + `create`;
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.post<MaterialModel>(url, material.saveToAPI(), {headers: httpHeaders}).pipe(
            catchError(err => {
                console.error('CREATE ITEM', err.error);
                return throwError(err);
            }),
            finalize(() => this._isLoading$.next(false))
        );
    }

    createMaterialThroughXlsx(formData: any) {
        this._isLoading$.next(true);
        const url = this.API_URL + `create-through-xlsx`;
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.post<any>(url, formData, {headers: httpHeaders}).pipe(
            catchError(err => {
                console.error('CREATE ITEM', err.error);
                return throwError(err);
            }),
            finalize(() => this._isLoading$.next(false))
        );
    }

    updateMaterial(material: MaterialModel): Observable<MaterialModel> {
        this._isLoading$.next(true);
        const url = this.API_URL + 'update';
        const httpHeader = this.httpUtils.getHTTPHeaders();
        return this.http.patch<MaterialModel>(url, material.saveToAPI(), {headers: httpHeader}).pipe(
            catchError(err => {
                console.error('UPDATE ITEM', material, err);
                return throwError(err);
            }),
            finalize(() => this._isLoading$.next(false))
        );
    }

    getItemById(id: number): Observable<MaterialModel> {
        this._isLoading$.next(true);
        const url = this.API_URL + `show/${id}`;
        return this.http.get<MaterialModel>(url).pipe(
            catchError(err => {
                // this._errorMessage.next(err);
                console.error('GET ITEM BY IT', id, err);
                return of(new MaterialModel());
            }),
            finalize(() => this._isLoading$.next(false))
        );
    }

    validationName(value: string, id: number): Observable<boolean> {
        this._isLoading$.next(true);
        const url = this.API_URL + `validate-name?name=${value}&id=${id}`;
        return this.http.get<boolean>(url).pipe(
            catchError(() => {
                return of(true);
            }),
            finalize(() => this._isLoading$.next(false))
        );
    }

    uploadMaterialMedia(formData: any) {
        this._isLoading$.next(true);
        const url = this.API_URL_MASTER + `upload-file`;
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.post<any>(url, formData, {headers: httpHeaders}).pipe(
            catchError(err => {
                console.error('CREATE ITEM', err.error);
                return throwError(err);
            }),
            finalize(() => this._isLoading$.next(false))
        );
    }

    getTemplateXlsx(): any {
        const url = this.API_URL + `download-template-create`;
        return this.http.get(url, {responseType: 'blob'});
    }

    get isLoading$() {
        return this._isLoading$.asObservable();
    }

    get isLoadingOnboarding$() {
        return this._isLoadingOnboarding$.asObservable();
    }
}
